import './index.scss';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { unflatten } from 'flat';
import { Button, Dimmer, Loader } from 'semantic-ui/semantic-ui-imports';
import { toast } from 'react-semantic-toasts';
import { useTranslation } from 'react-i18next';
import { delay } from 'utils';
import {
  cancelAggregateRequests,
  getAggregateCategories,
  getAggregateHeaders,
  getAggregates,
  getAggregatesBatchRequest,
} from 'services/aggregate';
import { getCsrfToken } from 'services/request';
import ProductTable, {
  DefaultAggregateCategories,
} from '../../components/ProductTable';
import ProductFilters from '../../components/ProductFilters';
import { Aggregate, AggregateCategory } from '../../Types/Aggregates';
import { useData } from '../../contexts/data';
import { formatAggregateCalcData } from '../../services/aggregateUtils';
import { useAuth } from '../../contexts/auth';
import { PromptToSignInContext } from '../../contexts/promptToSignIn';

type AggregateCategories = {
  aggregates: Array<Aggregate>;
} & AggregateCategory;

const ChooseProductPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    setActiveUserInputs,
    activeUserInputs,
    setActiveAggregate,
    setAggregates,
    aggregates,
    latestCalcResults,
    setLatestCalcResults,
    setSelectedResultId,
  } = useData();
  const { isExpert, user, isAuthenticated } = useAuth();
  const { promptToSignIn } = React.useContext(PromptToSignInContext);

  const [categories, setCategories] = React.useState<AggregateCategory[]>(
    DefaultAggregateCategories
  );

  const [aggregateCategories, setAggregateCategories] = React.useState<
    AggregateCategory[]
  >();
  const [selectedAggregate, setSelectedAggregate] = React.useState<Aggregate>();
  const { resetStorageData } = useData();

  const [hasRequested, setHasRequested] = React.useState<boolean>(false);
  const [isFiltering, setIsFiltering] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [calcBatches, setCalcBatches] = React.useState<
    (() => Promise<void>)[]
  >();
  const batchRef = React.useRef(calcBatches);

  const defaultValues = {
    'cooling.ambient.TIn': 26, // Sommer.Udeluft.Temperatur + 273.15 # Fra Celcius til Kelvin
    'cooling.ambient.RHIn': 80, // Sommer.Udeluft.Luftfugtighed / 100.0 # Fra % 0-100 til ratio 0-1
    'cooling.ambient.dp_external': 250,
    'cooling.ambient.VIn': 1000, // Luftmængder.Tilluft / 3600.0 # Fra m3/h til m3/s
    'cooling.indoor.TIn': 20, // Sommer.Fraluft.Temperatur + 273.15 # Fra Celcius til Kelvin
    'cooling.indoor.RHIn': 60, // Sommer.Fraluft.Luftfugtighed / 100.0 # Fra % 0-100 til ratio 0-1
    'cooling.indoor.calcTOut': false,
    'cooling.indoor.TOut': 26,
    'cooling.indoor.VIn': 1000, // Luftmængder.Fraluft / 3600.0 # Fra m3/h til m3/s
    'cooling.indoor.dp_external': 250,
    'heating.ambient.TIn': -10, // Vinter.Udeluft.Temperatur + 273.15 # Fra Celcius til Kelvin
    'heating.ambient.RHIn': 80, //  Vinter.Udeluft.Luftfugtighed / 100.0 # Fra % 0-100 til ratio 0-1
    'heating.ambient.dp_external': 250,
    'heating.ambient.VIn': 1000,
    'heating.indoor.TIn': 20, // Vinter.Fraluft.Temperatur + 273.15 # Fra Celcius til Kelvin
    'heating.indoor.RHIn': 40, // Vinter.Fraluft.Luftfugtighed / 100.0 # Fra % 0-100 til ratio 0-1
    'heating.indoor.calcTOut': false,
    'heating.indoor.TOut': 20,
    'heating.indoor.dp_external': 250,
    'heating.indoor.VIn': 1000,
    active_cooling: true,
    var_speed_pack: true,
    recirculation_module: false,
    supply_filter: 3,
    discharge_filter: 1,
    'reheater_config.coil_concentration': 20,
    'reheater_config.electric_capacity': 1000,
    'reheater_config.electric_reheater_cap': null,
    'reheater_config.coil_supply': 70, // reheater_config.coil_supply + 273.15 # Fra Celcius til Kelvin
    'reheater_config.coil_return': 40, // reheater_config.coil_return + 273.15 # Fra Celcius til Kelvin
    'reheater_config.reheater_type': 'NONE',
    'reheater_config.coil_media': 'WATER',
    dp_max_filter: 100,
    dp_max_filter_type: 'EN13053',
  };

  const formatSystems = (as: Array<Aggregate>) => {
    const newSystems: Array<AggregateCategories> = [];

    // add aggregates to matching category and sort by name (natural sort for numeric suffixes):
    aggregateCategories?.forEach((c: AggregateCategory) =>
      newSystems.push({
        ...c,
        key: c.id || '',
        aggregates:
          as
            .filter((a) => a.category === c.id)
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, {
                numeric: true,
                sensitivity: 'base',
              })
            ) || [],
      })
    );

    setLatestCalcResults(newSystems);
    return newSystems;
  };

  const findAndReplaceAggregate = (
    id: string,
    newProps: any,
    sys: AggregateCategories[]
  ) => {
    const newSystems = [...sys];
    for (let i = 0; i < sys.length; i += 1) {
      const idx = sys[i].aggregates.findIndex(
        (x) => String(x.id) === String(id)
      );
      if (idx > -1) {
        newSystems[i].aggregates[idx] = {
          ...sys[i].aggregates[idx],
          ...newProps,
        };
        break;
      }
    }
    return newSystems;
  };

  const preFilterAggregates = (
    aggrs: Aggregate[],
    params: { [key: string]: any }
  ): Aggregate[] => {
    const filteredAs = aggrs
      .filter((a) => {
        // filter out all aggregates with compressors if active_cooling is false.
        if (!params.active_cooling && a.compressors?.length > 0) {
          return false;
        }

        // Check whether aggregate has any compressor allowing variable speed
        if (params.active_cooling) {
          if (
            params.var_speed_pack &&
            !a.compressors.some((c) => c.allow_var_speed === true)
          ) {
            // Variable speed requested, but none of compressors support it
            return false;
          }

          if (
            !params.var_speed_pack &&
            a.compressors.some((c) => c.allow_var_speed === true)
          ) {
            // Fixed speed requested, but at least one compressor is variable speed
            return false;
          }
        }

        // filter on min air flow
        const maxAirflow =
          params.cooling?.ambient?.VIn > params.cooling?.indoor?.VIn
            ? params.cooling?.ambient?.VIn
            : params.cooling?.indoor?.VIn;
        if (maxAirflow < a.min_qv) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        // Also sort by minimum air flow, so smallest ones are run first:
        if (a.min_qv > b.min_qv) {
          return 1;
        }

        if (a.min_qv < b.min_qv) {
          return -1;
        }

        return 0;
      });

    return filteredAs;
  };

  const cancelBatchRequests = async () => {
    const batches: (() => Promise<void>)[] = [];
    setCalcBatches(batches);
    batchRef.current = batches;
    cancelAggregateRequests(); // cancel ongoing requests
    await delay(1);
  };

  const filter = async (params: { [key: string]: any }) => {
    if (isFiltering) {
      await cancelBatchRequests();
    }

    await getCsrfToken();

    setIsFiltering(true);

    if (user && !isExpert) {
      setIsFiltering(false);
      // Temporarily only allow expert users to use page
      return;
    }

    const filteredAggregates = preFilterAggregates(aggregates, params);

    try {
      const formattedSystems = formatSystems(filteredAggregates);
      const flatParams = formatAggregateCalcData(params, true);
      const parsedParams: any = unflatten(flatParams);

      const batches = getAggregatesBatchRequest(
        filteredAggregates.map((a) => a.id),
        parsedParams || {},
        // on success
        (a: Aggregate) => {
          if (a) {
            const beenSolved =
              a?.system_data?.heating?.solved !== false &&
              a.system_data?.cooling?.solved !== false;
            const newSystems = findAndReplaceAggregate(
              a.id,
              { ...a, calculated: true, error: !beenSolved },
              formattedSystems
            );
            setLatestCalcResults([...newSystems]);
          }
        },
        // on error
        (id: string) => {
          if (id) {
            const newSystems = findAndReplaceAggregate(
              id,
              { calculated: true, error: true, system_data: {} },
              formattedSystems
            );
            setLatestCalcResults([...newSystems]);
          }
        }
      );

      // set current batches
      batchRef.current = batches;
      setCalcBatches(batches);
      setActiveUserInputs({ ...activeUserInputs, ...params });
    } catch (error) {
      toast({
        type: 'error',
        title: t('common.calculationFailed'),
        time: 4000,
        size: 'small',
      });
    }
  };

  const getAllAggregates = async () => {
    setIsLoading(true);
    if (!hasRequested) {
      setHasRequested(true);
    }
    try {
      const res = await getAggregates();
      setAggregates(res?.results?.length ? (res.results as Aggregate[]) : []);
    } catch (error) {
      toast({
        type: 'error',
        title: t('common.calculationFailed'),
        time: 4000,
        size: 'small',
      });
    }
    setIsLoading(false);
  };

  const getCategories = async () => {
    setIsLoading(true);
    try {
      const res = await getAggregateHeaders();
      if (res) {
        setCategories(res);
      }
    } catch (error) {
      toast({
        type: 'error',
        title: t('common.calculationFailed'),
        time: 4000,
        size: 'small',
      });
    }
    setIsLoading(false);
  };

  const getAggregateCats = async () => {
    setIsLoading(true);
    try {
      const res = await getAggregateCategories();
      if (res) {
        setAggregateCategories(res);
      }
    } catch (error) {
      toast({
        type: 'error',
        title: t('common.calculationFailed'),
        time: 4000,
        size: 'small',
      });
    }
    setIsLoading(false);
  };

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated]);

  // Clear selectedResultId on mount - alternativeTo flag is sufficient for tracking
  React.useEffect(() => {
    setSelectedResultId(null);
  }, []);

  // listen for reload on index page. Reset local storage data, when so.
  React.useEffect(() => {
    getAggregateCats();

    getCategories();
    const listener = () => {
      if (window.location.pathname === '/') {
        // clear cache
        resetStorageData();
      }
    };
    window.addEventListener('unload', listener, { capture: true });
    return () => {
      window.removeEventListener('unload', listener, true);
    };
  }, []);

  React.useEffect(() => {
    if (!hasRequested && aggregateCategories?.length) {
      if (!isAuthenticated) {
        promptToSignIn();
        return;
      }

      if (user && !isExpert) {
        // Temporarily only allow expert users to use page
        return;
      }

      getAllAggregates();
    }
  }, [aggregateCategories, isAuthenticated, isExpert]);

  // when there is changes to calculations batches, this handles the processing
  const processNextCalcBatch = async (batches: (() => Promise<void>)[]) => {
    await batches[0]();
    if (batchRef.current === batches) {
      const newBatches = [...batches];
      newBatches.splice(0, 1); // this triggers next to process
      setCalcBatches(newBatches);
      batchRef.current = newBatches;
    }
  };

  // listen to calcBatches in order to process next batch.
  React.useEffect(() => {
    if (calcBatches?.length) {
      processNextCalcBatch(calcBatches);
    } else {
      setIsFiltering(false);
    }
  }, [calcBatches]);

  return (
    <div className="choose-product-page">
      <h2 className="choose-product-page__title">
        {t('chooseProductPage.title')}
      </h2>
      <ProductFilters
        onSubmit={filter}
        defaultValues={
          activeUserInputs && Object.keys(activeUserInputs).length
            ? activeUserInputs
            : defaultValues
        }
      />
      {latestCalcResults && !!latestCalcResults.length && (
        <ProductTable
          items={latestCalcResults}
          categories={categories}
          onSelect={setSelectedAggregate}
          selectedAggregate={selectedAggregate}
        />
      )}
      {!isLoading && hasRequested && !latestCalcResults?.length && (
        <p className="choose-product-page__no-results">
          {t('common.noResults')}
        </p>
      )}
      <div className="choose-product-page__nav-btns footer fixed-footer">
        <Button
          primary
          disabled={!selectedAggregate?.name}
          onClick={() => {
            cancelAggregateRequests();
            // Commit aggregate selection only on Continue
            setActiveAggregate(selectedAggregate || null);
            navigate(`/configure/${selectedAggregate?.id}`);
          }}
        >
          {t('navigation.proceed')} {'>'}
        </Button>
      </div>
      {isLoading && (
        <Dimmer inverted active page>
          <Loader size="large">Loading</Loader>
        </Dimmer>
      )}
    </div>
  );
};

export default ChooseProductPage;
